import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { normalizeLoginIdentifier } from '@/lib/loginIdentifier';
import * as repo from '../data/usersRepository';
import { useUsersMutation } from '../hooks/useUsers';
import type { MinisterOption, UserOverview } from '../types';
import { MinisterPicker } from './MinisterPicker';

const USERNAME_PATTERN = /^[a-z0-9][a-z0-9._-]{2,39}$/;
const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

interface EditUserDialogProps {
  user: UserOverview | null;
  onOpenChange: (open: boolean) => void;
}

export function EditUserDialog({ user, onOpenChange }: EditUserDialogProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [minister, setMinister] = useState<MinisterOption | null>(null);

  useEffect(() => {
    if (!user) return;
    setName(user.full_name ?? '');
    setEmail(user.display_email ?? '');
    setUsername(user.username ?? '');
    setMinister(user.minister_id ? { id: user.minister_id, full_name: user.minister_name, adb_code: user.minister_adb_code ?? '' } : null);
  }, [user]);

  const save = useUsersMutation(async () => {
    if (!user) return;
    const basicChanged = name.trim() !== (user.full_name ?? '') || email.trim().toLowerCase() !== (user.display_email ?? '');
    if (basicChanged) await repo.updateUserBasic(user.id, name, email);
    if (normalized && normalized !== (user.username ?? '')) await repo.renameUser(user.id, normalized);
    if ((minister?.id ?? null) !== (user.minister_id ?? null)) await repo.linkUserMinister(user.id, minister?.id ?? null);
  }, { success: 'Usuario actualizado' });

  const normalized = normalizeLoginIdentifier(username);
  const renaming = !!user && normalized !== (user.username ?? '');
  const usernameError = username && !USERNAME_PATTERN.test(normalized) ? 'Entre 3 y 40 caracteres: minúsculas, números, punto, guion.' : null;
  const emailError = email && !EMAIL_PATTERN.test(email.trim()) ? 'El correo no es válido.' : null;
  const canSave = name.trim().length >= 3 && !usernameError && !emailError && !save.isPending;

  return (
    <Dialog open={!!user} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <form
          onSubmit={(e) => { e.preventDefault(); if (canSave) save.mutate(undefined, { onSuccess: () => onOpenChange(false) }); }}
          className="space-y-4"
        >
          <DialogHeader>
            <DialogTitle>Editar usuario</DialogTitle>
            <DialogDescription>Los roles se cambian aparte, con «Roles».</DialogDescription>
          </DialogHeader>

          <div className="space-y-1.5">
            <Label htmlFor="edit-name">Nombre completo</Label>
            <Input id="edit-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-email">Correo de contacto</Label>
            <Input id="edit-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            {emailError && <p className="text-xs text-destructive">{emailError}</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-username">Usuario</Label>
            <Input id="edit-username" value={username} className="font-mono" autoCapitalize="none" spellCheck={false}
              onChange={(e) => setUsername(e.target.value)} />
            {usernameError && <p className="text-xs text-destructive">{usernameError}</p>}
            {renaming && !usernameError && (
              <Alert><AlertDescription>Cambiar el usuario cambia cómo inicia sesión: avisa a la persona.</AlertDescription></Alert>
            )}
          </div>
          <div className="space-y-1.5">
            <Label>Ministro vinculado</Label>
            <MinisterPicker value={minister} onChange={setMinister} />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={!canSave}>
              {save.isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
              Guardar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
